import type {OverrideOrDefaultData, CompRef, Pointer} from '@wix/document-services-types'
import {guidUtils} from '@wix/santa-core-utils'
import type {
    ManifestExtensionAPI,
    InnerElementsExtensionAPI,
    ComponentsExtensionAPI,
    HooksExtensionApi,
    SlotsExtensionAPI,
    DataModelExtensionAPI,
    StructureExtensionAPI,
    DataExtensionAPI,
    SchemaExtensionAPI
} from '@wix/document-manager-extensions'
import {
    getUpdatedDataItemAtNestedPath,
    getDataToUpdate,
    getSlotInfoForComponent,
    populateSlotWithSystemContainer
} from './slotPopulationUtils'
import type {CreateExtArgs} from '@wix/document-manager-core'
import {DATA_TYPES} from '../../../constants/constants'
import _ from 'lodash'
import {COMPONENTS_HOOKS} from '../../../hooks'

type PropertyItem = Record<string, {} | []>

const getContainerIdFromSlotObject = (
    createExtArgs: CreateExtArgs,
    innerElementPath: string[] | undefined,
    componentRef: Pointer,
    sourceSlotGuid: string
) => {
    const {extensionAPI} = createExtArgs
    const {innerElements} = extensionAPI as InnerElementsExtensionAPI
    const {slots} = extensionAPI as SlotsExtensionAPI

    if (innerElementPath) {
        const slotsData = innerElements.getInnerElementSlots(componentRef as CompRef, innerElementPath)
        return slotsData?.[sourceSlotGuid]?.id
    }

    const slotsData = slots.getSlotsData(componentRef as CompRef)
    return slotsData?.[sourceSlotGuid]?.id
}

const duplicateSystemContainer = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    duplicatedContainerItem: OverrideOrDefaultData
) => {
    const {prop, path, nestedDataPath, value} = duplicatedContainerItem
    const {extensionAPI, pointers} = createExtArgs
    const {components} = extensionAPI as ComponentsExtensionAPI
    const {hooks} = extensionAPI as HooksExtensionApi
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {data} = extensionAPI as DataExtensionAPI

    const newSlotGuid = guidUtils.getGUID()
    data.injectData({pointer: componentRef, path, nestedDataPath, partialNewData: {[prop]: newSlotGuid}})

    const sourceContainerId = getContainerIdFromSlotObject(createExtArgs, path, componentRef, value)

    if (sourceContainerId) {
        const sourceContainerPointer = pointers.getPointer(sourceContainerId, 'DESKTOP') as CompRef
        const duplicatedContainerRef = components.getComponentToAddRef()

        hooks.executeHook({
            event: COMPONENTS_HOOKS.DUPLICATE.COMPONENT.id,
            // @ts-ignore
            compRefToAdd: duplicatedContainerRef,
            componentPointer: sourceContainerPointer,
            newContainerPointer: componentRef as CompRef
        })

        const {pageId, slotDataItem, slotsPointer} = getSlotInfoForComponent(createExtArgs, componentRef, path)

        slotDataItem.slots[newSlotGuid] = duplicatedContainerRef.id
        dataModel.addItem(slotDataItem, DATA_TYPES.slots, pageId, slotsPointer!.id)
    }
}

const processNewItem = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    containersList: OverrideOrDefaultData[]
) => {
    containersList.forEach(container => {
        const slotName = container.prop
        populateSlotWithSystemContainer(createExtArgs, componentRef, slotName, container)
    })
}

const getDuplicatedContainers = (
    optionalContainerToDuplicate: OverrideOrDefaultData[],
    updatedDataItem: PropertyItem
): OverrideOrDefaultData[] => {
    //group all containers inside a specific array
    const groupedBySignature = _.groupBy(optionalContainerToDuplicate, container => {
        const keys = container.nestedDataPath?.map(({key}: {key: string}) => key).join('.') ?? ''
        return `${container.prop}:${keys}`
    })

    return _.flatMap(groupedBySignature, containers => {
        // a single container cannot be a duplicate - at least two items with the same path structure must exist
        if (containers.length <= 1) {
            return []
        }

        return containers.filter(container => {
            const currentLevel = getUpdatedDataItemAtNestedPath(container.nestedDataPath ?? [], updatedDataItem)
            return !currentLevel
        })
    })
}

const processDuplicateItems = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    updatedDataItem: PropertyItem,
    optionalContainerToDuplicate: OverrideOrDefaultData[]
) => {
    const duplicatedContainers = getDuplicatedContainers(optionalContainerToDuplicate, updatedDataItem)
    duplicatedContainers.forEach((duplicatedContainerItem: OverrideOrDefaultData) => {
        duplicateSystemContainer(createExtArgs, componentRef, duplicatedContainerItem)
    })
}

const handleElementDataUpdate = (
    createExtArgs: CreateExtArgs,
    componentRef: CompRef,
    updatedDataItem: PropertyItem,
    containersListIncludeInnerElement: OverrideOrDefaultData[]
) => {
    const containerToPopulate = containersListIncludeInnerElement.filter(({value, nestedDataPath}) => {
        const currentLevel = getUpdatedDataItemAtNestedPath(nestedDataPath ?? [], updatedDataItem)
        return !value && !currentLevel?._id
    })

    const optionalContainerToDuplicate = containersListIncludeInnerElement.filter(({value}) => !!value)

    processNewItem(createExtArgs, componentRef, containerToPopulate)
    processDuplicateItems(createExtArgs, componentRef, updatedDataItem, optionalContainerToDuplicate)
}

const validateNoDuplicateIds = (data: PropertyItem) => {
    const seenIds = new Set<string>()
    const duplicateIds = new Set<string>()

    const collectIds = (items: any[]) => {
        items.forEach(item => {
            if (item._id) {
                if (seenIds.has(item._id)) {
                    duplicateIds.add(item._id)
                } else {
                    seenIds.add(item._id)
                }
            }
        })
    }

    Object.values(data).forEach(value => {
        if (Array.isArray(value)) {
            collectIds(value)
        }
    })

    if (duplicateIds.size > 0) {
        const duplicateIdsList = Array.from(duplicateIds).join(', ')
        throw new Error(`Duplicate id values found in update data: ${duplicateIdsList}`)
    }
}

export const builderComponentsDataUpdateHook = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    updatedDataItem: PropertyItem
) => {
    const {extensionAPI} = createExtArgs
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const {schemaAPI} = extensionAPI as SchemaExtensionAPI
    const {components} = extensionAPI as StructureExtensionAPI

    const componentType = components.getComponentType(componentRef)

    if (!schemaAPI.isSchemaCollectionRegistered(componentType)) {
        return
    }

    validateNoDuplicateIds(updatedDataItem)

    const containersListIncludeInnerElement = getDataToUpdate(manifest, componentRef, true)

    handleElementDataUpdate(createExtArgs, componentRef as CompRef, updatedDataItem, containersListIncludeInnerElement)
}
